/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import HtmlTemplatePreview from './HtmlTemplatePreview'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import { EmailTemplatesAction } from 'store/emailTemplates/EmailTemplatesAction'
import { OfferEmailTemplatesAction } from 'store/offers/OfferEmailTemplatesAction'
import { EmailTemplate } from 'types/entities/EmailTemplate'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import { toast } from 'utils/toastUtils'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import {
    ArrowDownTrayIcon,
    ArrowTopRightOnSquareIcon,
    DocumentDuplicateIcon,
    EnvelopeIcon,
    EyeIcon,
    FolderIcon,
    PlusIcon,
    StarIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import UiEmptyState from 'components/Ui/UiEmptyState'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import { ModalSize } from 'types/enums/ModalSize'
import EmailTemplateFormModal from './forms/EmailTemplateFormModal'
import { Offer } from 'types/entities/Offer'
import { Lead } from 'types/entities/Lead'
import { Company } from 'types/entities/Company'
import { useEmailPlaceholders } from 'utils/email/useEmailPlaceholders'
import { translate } from 'utils/utility'

interface EmailTemplatesTabProps {
    modelName: string
    modelId: number
    context?: {
        offer?: Offer | null
        lead?: Lead | null
        company?: Company | null
    }
}

const EmailTemplatesTab: React.FC<EmailTemplatesTabProps> = ({ modelName, modelId, context }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
    const [allTemplates, setAllTemplates] = useState<EmailTemplate[]>([])

    const offerTemplates = useTypedSelector(
        (state) => state.emailTemplatesState.templatesByModel[modelName]?.[modelId] || []
    )
    // Show newest templates on top
    const sortedOfferTemplates = [...offerTemplates].reverse()

    const otherTemplates = allTemplates.filter((templ) => templ.model_id !== modelId)
    const selectedTemplate = offerTemplates.find((template) => template.id === selectedTemplateId)
        ?? otherTemplates.find((template) => template.id === selectedTemplateId)
        ?? null

    const { placeholders, resolvePlaceholders } = useEmailPlaceholders(context)

    useEffect(() => {
        dispatch(EmailTemplatesAction.fetchTemplates(modelName, modelId))
    }, [dispatch, modelName, modelId])

    useEffect(() => {
        dispatch(OfferEmailTemplatesAction.fetchTemplates()).then((result) => setAllTemplates(result))
    }, [dispatch])

    const isFromCurrentOffer = (template: EmailTemplate) => template.model_id === modelId

    const onAddHandler = () => {
        dispatch(
            openSecondaryModal(
                <EmailTemplateFormModal
                    modelName={modelName}
                    modelId={modelId}
                    context={context}
                    onSuccess={() => dispatch(closeSecondaryModal())}
                />,
                {
                    modalSize: ModalSize.Medium,
                    closeModal: () => dispatch(closeSecondaryModal()),
                },
                t('Add Email Template')
            )
        )
    }

    const onEditHandler = (template: EmailTemplate) => {
        dispatch(
            openSecondaryModal(
                <EmailTemplateFormModal
                    modelName={modelName}
                    modelId={modelId}
                    template={template}
                    context={context}
                    onSuccess={() => dispatch(closeSecondaryModal())}
                />,
                {
                    modalSize: ModalSize.Medium,
                    closeModal: () => dispatch(closeSecondaryModal()),
                },
                t('Edit Email Template')
            )
        )
    }

    const onDuplicateHandler = (template: EmailTemplate) => {
        const duplicated: Partial<EmailTemplate> = {
            model_name: modelName,
            model_id: modelId,
            name: `${template.name} (${t('Copy')})`,
            subject: template.subject,
            content: template.content,
            category: template.category,
        }
        dispatch(
            openSecondaryModal(
                <EmailTemplateFormModal
                    modelName={modelName}
                    modelId={modelId}
                    template={duplicated as EmailTemplate}
                    context={context}
                    onSuccess={() => dispatch(closeSecondaryModal())}
                />,
                {
                    modalSize: ModalSize.Medium,
                    closeModal: () => dispatch(closeSecondaryModal()),
                },
                t('Duplicate Email Template')
            )
        )
    }

    const onDeleteHandler = (template: EmailTemplate) => {
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Delete Template')}
                    message={t('Are you sure you want to delete this template?')}
                    onConfirm={async () => {
                        try {
                            await dispatch(EmailTemplatesAction.fetchDeleteTemplate(template))
                            if (selectedTemplate?.id === template.id) {
                                setSelectedTemplateId(null)
                            }
                            toast.success(t('Template deleted'))
                            dispatch(closeSecondaryModal())
                        } catch {
                            toast.error(t('Failed to delete the template'))
                            dispatch(closeSecondaryModal())
                        }
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    const onExportHandler = async (template: EmailTemplate) => {
        const placeholderMap: Record<string, string> = {}
        placeholders.forEach((placeholder) => {
            placeholderMap[placeholder.key] = placeholder.value?.toString() || ''
        })
        try {
            await EmailTemplatesAction.exportToEml(template, placeholderMap)
            toast.success(t('Template exported to EML'))
        } catch {
            toast.error(t('Failed to export the template'))
        }
    }

    const getResolvedPreview = (template: EmailTemplate) => {
        return {
            subject: resolvePlaceholders(template.subject),
            content: resolvePlaceholders(template.content),
        }
    }

    const hasNoTemplatesAtAll = sortedOfferTemplates.length === 0 && otherTemplates.length === 0

    const renderTemplateItem = (template: EmailTemplate, isCurrentOffer: boolean) => (
        <div
            key={template.id}
            className={clsx(
                'TemplateListItem flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors',
                selectedTemplate?.id === template.id
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
            )}
            onClick={() => setSelectedTemplateId(template.id)}
        >
            <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{template.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {resolvePlaceholders(template.subject)}
                </div>

                {context?.offer?.packagesCart?.package?.name && (
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 truncate flex items-center gap-1">
                        <span className="opacity-70">{t('Ticket type')}:</span>
                        <span>{translate(context.offer.packagesCart.package.name)}</span>
                    </div>
                )}
                {!isCurrentOffer && (
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                        {t('From Offer ID')}: {template.model_id}
                    </div>
                )}
            </div>
            <div className="flex gap-1 ml-2">
                <UiButton
                    title={t('Preview')}
                    icon={<EyeIcon className="w-4 h-4" />}
                    style={UiButtonStyle.LIGHT}
                    size={UiButtonSize.Normal}
                    onClick={() => setSelectedTemplateId(template.id)}
                />
            </div>
        </div>
    )

    return (
        <div className="EmailTemplatesTab flex flex-col gap-4 pt-4 h-full overflow-hidden">
            <div className="flex justify-between items-center shrink-0 px-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('Email Templates')}</h2>
                <div className="flex gap-2">
                    {selectedTemplate && (
                        <UiButton
                            title={t('Export EML')}
                            icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                            style={UiButtonStyle.Secondary}
                            size={UiButtonSize.Normal}
                            onClick={() => onExportHandler(selectedTemplate)}
                        />
                    )}
                    <UiButton
                        title={t('Add Template')}
                        icon={<PlusIcon className="w-4 h-4" />}
                        style={UiButtonStyle.Primary}
                        size={UiButtonSize.Normal}
                        onClick={onAddHandler}
                    />
                </div>
            </div>

            <div className="flex-1 flex gap-4 overflow-hidden px-4">
                <div className={clsx('UiCard overflow-hidden flex flex-col', selectedTemplate ? 'w-1/2' : 'w-full')}>
                    {hasNoTemplatesAtAll ? (
                        <UiEmptyState
                            className="py-12"
                            icon={<EnvelopeIcon className="w-12 h-12 text-gray-400 dark:text-gray-600" />}
                            title={t('No email templates')}
                            description={t('Create your first email template for this record.')}
                        />
                    ) : (
                        <div className="flex-1 overflow-auto">
                            {sortedOfferTemplates.length > 0 && (
                                <div className="OfferTemplatesSection">
                                    <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 flex items-center gap-2">
                                        <StarIcon className="w-4 h-4 text-blue-500" />
                                        <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                                            {t("This Offer's Templates")}
                                        </span>
                                        <span className="text-[10px] text-blue-500 bg-blue-100 dark:bg-blue-900/50 px-1.5 py-0.5 rounded-full">
                                            {sortedOfferTemplates.length}
                                        </span>
                                    </div>
                                    {sortedOfferTemplates.map((template) => renderTemplateItem(template, true))}
                                </div>
                            )}

                            {otherTemplates.length > 0 && (
                                <div className="OtherTemplatesSection">
                                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                                        <FolderIcon className="w-4 h-4 text-gray-400" />
                                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t('Other Available Templates')}
                                        </span>
                                        <span className="text-[10px] text-gray-400 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">
                                            {otherTemplates.length}
                                        </span>
                                    </div>
                                    {otherTemplates.map((template) => renderTemplateItem(template, false))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {selectedTemplate && (
                    <div className="TemplatePreviewPanel w-1/2 UiCard overflow-hidden flex flex-col">
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-2 min-w-0">
                                <EnvelopeIcon className="w-5 h-5 text-blue-500 shrink-0" />
                                <span className="font-bold text-gray-900 dark:text-gray-100 truncate">{t('Preview')}</span>
                                {isFromCurrentOffer(selectedTemplate) ? (
                                    <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50 px-2 py-0.5 rounded-full shrink-0">
                                        {t('This Offer')}
                                    </span>
                                ) : (
                                    <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full shrink-0">
                                        {t('Other Offer')}
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                                {isFromCurrentOffer(selectedTemplate) ? (
                                    <>
                                        <UiButton
                                            title={t('Export EML')}
                                            icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                                            style={UiButtonStyle.Secondary}
                                            size={UiButtonSize.Normal}
                                            onClick={() => onExportHandler(selectedTemplate)}
                                        />
                                        <UiButton
                                            title={t('Duplicate')}
                                            icon={<DocumentDuplicateIcon className="w-4 h-4" />}
                                            style={UiButtonStyle.Secondary}
                                            size={UiButtonSize.Normal}
                                            onClick={() => onDuplicateHandler(selectedTemplate)}
                                        />
                                        <UiButton
                                            title={t('Open')}
                                            icon={<ArrowTopRightOnSquareIcon className="w-4 h-4" />}
                                            style={UiButtonStyle.Primary}
                                            size={UiButtonSize.Normal}
                                            onClick={() => onEditHandler(selectedTemplate)}
                                        />
                                    </>
                                ) : (
                                    <UiButton
                                        title={t('Use as Template')}
                                        icon={<DocumentDuplicateIcon className="w-4 h-4" />}
                                        style={UiButtonStyle.Primary}
                                        size={UiButtonSize.Normal}
                                        onClick={() => onDuplicateHandler(selectedTemplate)}
                                    />
                                )}
                                <UiButton
                                    title={t('Delete')}
                                    icon={<TrashIcon className="w-4 h-4" />}
                                    style={UiButtonStyle.Red}
                                    size={UiButtonSize.Normal}
                                    onClick={() => onDeleteHandler(selectedTemplate)}
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-hidden flex flex-col p-3">
                            <div className="space-y-3 flex-1 overflow-auto">
                                <div>
                                    <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                                        {t('Template Name')}
                                    </div>
                                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        {selectedTemplate.name}
                                    </div>
                                </div>

                                {selectedTemplate.category && (
                                    <div>
                                        <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                                            {t('Category')}
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                            {selectedTemplate.category}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                                        {t('Subject')}
                                    </div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-lg">
                                        {getResolvedPreview(selectedTemplate).subject || (
                                            <span className="text-gray-400 italic">({t('No subject')})</span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex-1 overflow-hidden flex flex-col">
                                    <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                                        {t('Content (Resolved)')}
                                    </div>
                                    <div className="flex-1 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 overflow-auto">
                                        <HtmlTemplatePreview title={t('Live Preview')} html={getResolvedPreview(selectedTemplate).content || ''} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default EmailTemplatesTab
